/**
 * useUpdatePaymentMethod - Hook for managing payment method update flow
 *
 * Handles the complete Stripe SetupIntent flow:
 * 1. Create SetupIntent via API
 * 2. Confirm with Stripe Elements
 * 3. Update subscription payment method
 * 4. Handle success/error states
 *
 * Supports both card payment and Express Checkout (Apple Pay/Google Pay).
 */

import {
  useCreateSetupIntentV1SubscriptionsSubscriptionIdSetupIntentPost,
  useUpdatePaymentMethodV1SubscriptionsSubscriptionIdPaymentMethodPatch,
} from '@/api/generated-catch-backend/subscriptions/subscriptions';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useCallback, useState } from 'react';
import type { StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';
import { useMe } from '@/context/me/me.context';

export type PaymentUpdateState = 'idle' | 'creating_setup_intent' | 'confirming' | 'updating' | 'success' | 'error';

interface UseUpdatePaymentMethodOptions {
  subscriptionId: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

interface UseUpdatePaymentMethodResult {
  state: PaymentUpdateState;
  error: string | null;
  clientSecret: string | null;
  updatePaymentMethod: (zipCode?: string) => Promise<void>;
  handleExpressCheckout: (event: StripeExpressCheckoutElementConfirmEvent) => Promise<void>;
  reset: () => void;
}

export function useUpdatePaymentMethod({
  subscriptionId,
  onSuccess,
  onError,
}: UseUpdatePaymentMethodOptions): UseUpdatePaymentMethodResult {
  const [state, setState] = useState<PaymentUpdateState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const stripe = useStripe();
  const elements = useElements();
  const { refetch: refetchMe } = useMe();

  const createSetupIntentMutation = useCreateSetupIntentV1SubscriptionsSubscriptionIdSetupIntentPost();
  const updatePaymentMethodMutation = useUpdatePaymentMethodV1SubscriptionsSubscriptionIdPaymentMethodPatch();

  const updatePaymentMethod = useCallback(
    async (zipCode?: string) => {
      if (!stripe || !elements) {
        setError('Stripe not initialized');
        setState('error');
        return;
      }

      try {
        // Step 1: Validate the form
        const { error: submitError } = await elements.submit();
        if (submitError) {
          setError(submitError.message ?? 'Please check your payment information');
          setState('error');
          onError?.(submitError.message ?? 'Please check your payment information');
          return;
        }

        // Step 2: Create SetupIntent
        setState('creating_setup_intent');
        const setupIntentResponse = await createSetupIntentMutation.mutateAsync({
          subscriptionId,
        });
        setClientSecret(setupIntentResponse.clientSecret);

        // Step 3: Confirm SetupIntent with Stripe
        setState('confirming');
        const { error: confirmError, setupIntent } = await stripe.confirmSetup({
          elements,
          clientSecret: setupIntentResponse.clientSecret,
          confirmParams: {
            payment_method_data: {
              billing_details: {
                address: {
                  country: 'US',
                  postal_code: zipCode,
                },
              },
            },
          },
          redirect: 'if_required',
        });

        if (confirmError) {
          setError(confirmError.message ?? 'Card verification failed');
          setState('error');
          onError?.(confirmError.message ?? 'Card verification failed');
          return;
        }

        if (!setupIntent || setupIntent.status !== 'succeeded') {
          setError('SetupIntent confirmation failed');
          setState('error');
          onError?.('SetupIntent confirmation failed');
          return;
        }

        // Step 4: Update subscription payment method and retry invoice
        setState('updating');
        await updatePaymentMethodMutation.mutateAsync({
          subscriptionId,
          data: {
            setupIntentId: setupIntentResponse.setupIntentId,
          },
        });

        // Refetch /me to get updated userLevel (now 'member')
        // This prevents paywall from reappearing due to cached 'canceled' state
        await refetchMe();

        // Success!
        setState('success');
        setError(null);
        onSuccess?.();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Your card was declined. Please try a different payment method.';
        setError(errorMessage);
        setState('error');
        onError?.(errorMessage);
      }
    },
    [stripe, elements, subscriptionId, createSetupIntentMutation, updatePaymentMethodMutation, refetchMe, onSuccess, onError]
  );

  /**
   * Handle Express Checkout (Apple Pay/Google Pay) confirmation
   */
  const handleExpressCheckout = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      if (!stripe || !elements) {
        setError('Stripe not initialized');
        setState('error');
        return;
      }

      try {
        // Submit elements first
        const { error: submitError } = await elements.submit();
        if (submitError) {
          setError(submitError.message ?? 'Payment failed');
          setState('error');
          onError?.(submitError.message ?? 'Payment failed');
          return;
        }

        // Create SetupIntent
        setState('creating_setup_intent');
        const setupIntentResponse = await createSetupIntentMutation.mutateAsync({
          subscriptionId,
        });
        setClientSecret(setupIntentResponse.clientSecret);

        // Get zip code from billing details
        const zipCode = event.billingDetails?.address?.postal_code || '';

        // Confirm SetupIntent with Stripe
        setState('confirming');
        const { error: confirmError, setupIntent } = await stripe.confirmSetup({
          elements,
          clientSecret: setupIntentResponse.clientSecret,
          confirmParams: {
            payment_method_data: {
              billing_details: {
                address: {
                  country: 'US',
                  postal_code: zipCode,
                },
              },
            },
          },
          redirect: 'if_required',
        });

        if (confirmError) {
          setError(confirmError.message ?? 'Card verification failed');
          setState('error');
          onError?.(confirmError.message ?? 'Card verification failed');
          return;
        }

        if (!setupIntent || setupIntent.status !== 'succeeded') {
          setError('SetupIntent confirmation failed');
          setState('error');
          onError?.('SetupIntent confirmation failed');
          return;
        }

        // Update subscription payment method and retry invoice
        setState('updating');
        await updatePaymentMethodMutation.mutateAsync({
          subscriptionId,
          data: {
            setupIntentId: setupIntentResponse.setupIntentId,
          },
        });

        // Refetch /me to get updated userLevel (now 'member')
        await refetchMe();

        // Success!
        setState('success');
        setError(null);
        onSuccess?.();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Your card was declined. Please try a different payment method.';
        setError(errorMessage);
        setState('error');
        onError?.(errorMessage);
      }
    },
    [stripe, elements, subscriptionId, createSetupIntentMutation, updatePaymentMethodMutation, refetchMe, onSuccess, onError]
  );

  const reset = useCallback(() => {
    setState('idle');
    setError(null);
    setClientSecret(null);
  }, []);

  return {
    state,
    error,
    clientSecret,
    updatePaymentMethod,
    handleExpressCheckout,
    reset,
  };
}
